// ⌘K command palette — modal scrim + suggestion list. Hand-written (~80
// lines, no cmdk lib) so we don't pull a dep for one feature. Suggestions
// come from the parent so the palette stays presentational; it doesn't know
// about Demo.tsx or any specific question source.
import { useEffect, useRef, useState } from 'react';

export interface PaletteSection {
  label: string;
  items: PaletteItem[];
}

export interface PaletteItem {
  id: string;
  text: string;
  short?: string;
  icon?: string;
}

interface Props {
  open: boolean;
  initialQuery?: string;
  sections: PaletteSection[];
  onClose: () => void;
  onSubmit: (query: string) => void;
}

export function CommandPalette({ open, initialQuery, sections, onClose, onSubmit }: Props) {
  const [query, setQuery] = useState(initialQuery ?? '');
  const inputRef = useRef<HTMLInputElement>(null);
  const flatItems = sections.flatMap((s) => s.items);
  const [activeIdx, setActiveIdx] = useState(0);

  useEffect(() => {
    if (!open) return;
    setQuery(initialQuery ?? '');
    setActiveIdx(0);
    // Defer focus to next frame so the input is mounted before we focus it
    // (React strict-mode double-mounts in dev would otherwise focus a stale
    // node).
    const t = setTimeout(() => inputRef.current?.focus(), 0);
    return () => clearTimeout(t);
  }, [open, initialQuery]);

  if (!open) return null;

  const submitNow = () => {
    const text = query.trim();
    if (text) {
      onSubmit(text);
    } else if (flatItems[activeIdx]) {
      onSubmit(flatItems[activeIdx].text);
    }
    onClose();
  };

  const onKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Enter') { e.preventDefault(); submitNow(); }
    else if (e.key === 'ArrowDown') { e.preventDefault(); setActiveIdx((i) => Math.min(i + 1, flatItems.length - 1)); }
    else if (e.key === 'ArrowUp') { e.preventDefault(); setActiveIdx((i) => Math.max(i - 1, 0)); }
    else if (e.key === 'Escape') { e.preventDefault(); onClose(); }
  };

  let runningIdx = 0;
  return (
    <div className="atlas-palette-scrim" onClick={onClose} role="dialog" aria-label="Command palette">
      <div className="atlas-palette" onClick={(e) => e.stopPropagation()}>
        <div className="atlas-palette__input-row">
          <span className="atlas-palette__icon" aria-hidden="true">⌕</span>
          <input
            ref={inputRef}
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            onKeyDown={onKeyDown}
            placeholder="Ask about HRSA programs, places, or grants…"
            className="atlas-palette__input"
            aria-label="Search HRSA data"
          />
          <span className="atlas-palette__hint">⏎ ask</span>
        </div>
        {sections.map((sec) => (
          <div key={sec.label}>
            <div className="atlas-palette__section">{sec.label}</div>
            {sec.items.map((item) => {
              const idx = runningIdx++;
              return (
                <button
                  key={item.id}
                  className={`atlas-palette__row ${idx === activeIdx ? 'atlas-palette__row--active' : ''}`}
                  onClick={() => { onSubmit(item.text); onClose(); }}
                  onMouseEnter={() => setActiveIdx(idx)}
                >
                  <span className="atlas-palette__row-icon" aria-hidden="true">{item.icon ?? '◇'}</span>
                  <span>{item.text}</span>
                  {item.short && <span className="atlas-palette__short">{item.short}</span>}
                </button>
              );
            })}
          </div>
        ))}
      </div>
    </div>
  );
}
